"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Box, IconButton, Dialog, DialogContent, useMediaQuery, useTheme } from "@mui/material"
import { IconChevronLeft, IconChevronRight, IconX, IconInfoCircle } from "@tabler/icons-react"
import ImageOptimizer from "./ImageOptimizer"

interface ImageCarouselProps {
    images: string[]
    onInfoClick?: () => void
    showInfoButton?: boolean
    enableFullscreen?: boolean
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
    images,
    onInfoClick,
    showInfoButton = false,
    enableFullscreen = false,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [fullscreenOpen, setFullscreenOpen] = useState(false)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const handlePrev = (e?: React.MouseEvent) => {
        if (e) e.stopPropagation()
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1))
    }

    const handleNext = (e?: React.MouseEvent) => {
        if (e) e.stopPropagation()
        setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1))
    }

    const handleImageClick = () => {
        if (enableFullscreen) {
            setFullscreenOpen(true)
        }
    }

    const handleCloseFullscreen = () => {
        setFullscreenOpen(false)
    }

    const handleInfoButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onInfoClick) onInfoClick()
    }

    // Optimización: Precarga la imagen actual y las adyacentes
    useEffect(() => {
        if (images.length > 1) {
            const preloadImages = [
                images[currentIndex],
                images[(currentIndex + 1) % images.length],
                images[currentIndex === 0 ? images.length - 1 : currentIndex - 1],
            ]

            preloadImages.forEach((src) => {
                if (src) {
                    const img = new Image()
                    img.src = src
                }
            })
        }
    }, [currentIndex, images])

    if (images.length === 0) {
        return (
            <Box
                sx={{
                    width: "100%",
                    paddingTop: "100%",
                    position: "relative",
                    backgroundColor: "#f0f0f0",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    No hay imágenes disponibles
                </Box>
            </Box>
        )
    }

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    paddingTop: "100%",
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: enableFullscreen ? "pointer" : "default",
                }}
                onClick={handleImageClick}
            >
                <ImageOptimizer
                    src={images[currentIndex] || "/placeholder.svg"}
                    alt={`Imagen ${currentIndex + 1}`}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                />

                {images.length > 1 && (
                    <>
                        <IconButton
                            onClick={handlePrev}
                            sx={{
                                position: "absolute",
                                left: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                },
                            }}
                        >
                            <IconChevronLeft size={24} />
                        </IconButton>

                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                },
                            }}
                        >
                            <IconChevronRight size={24} />
                        </IconButton>

                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 8,
                                left: "50%",
                                transform: "translateX(-50%)",
                                display: "flex",
                                gap: 1,
                            }}
                        >
                            {images.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: index === currentIndex ? "#0d6336" : "rgba(255, 255, 255, 0.7)",
                                    }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {showInfoButton && (
                    <IconButton
                        onClick={handleInfoButtonClick}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                            },
                        }}
                    >
                        <IconInfoCircle size={20} color="#0d6336" />
                    </IconButton>
                )}
            </Box>

            <Dialog
                open={fullscreenOpen}
                onClose={handleCloseFullscreen}
                maxWidth="xl"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.9)",
                        boxShadow: "none",
                        m: isMobile ? 0 : 2,
                        height: isMobile ? "100%" : "calc(100% - 32px)",
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.9)",
                    },
                }}
                onClick={handleCloseFullscreen}
            >
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        handleCloseFullscreen()
                    }}
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        color: "white",
                        zIndex: 1,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    }}
                >
                    <IconX size={24} />
                </IconButton>

                <DialogContent
                    sx={{ p: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ImageOptimizer
                        src={images[currentIndex] || "/placeholder.svg"}
                        alt={`Imagen ${currentIndex + 1} a pantalla completa`}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />

                    {images.length > 1 && (
                        <>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handlePrev()
                                }}
                                sx={{
                                    position: "absolute",
                                    left: 16,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    },
                                }}
                            >
                                <IconChevronLeft size={32} />
                            </IconButton>

                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleNext()
                                }}
                                sx={{
                                    position: "absolute",
                                    right: 16,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    },
                                }}
                            >
                                <IconChevronRight size={32} />
                            </IconButton>

                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    display: "flex",
                                    gap: 1,
                                }}
                            >
                                {images.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            backgroundColor: index === currentIndex ? "white" : "rgba(255, 255, 255, 0.5)",
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ImageCarousel

